import React, { useEffect, useMemo, useState } from "react"; // 212 §22 — `import React` is react/react-in-jsx-scope's own remedy (189 F.1)
import { X, Copy, Mail, Search, Filter } from "lucide-react";
import { api } from "../api";
import { useTranslation } from "../i18n";
import CollectiveSegmentFilter from "./CollectiveSegmentFilter";
import {
  makeSegmentEvaluator,
  buildDocStatusIndex,
  defaultSegmentFilter,
  hasEmail,
  addresseeName,
  chunk,
} from "../lib/collectiveSegments.mjs";
import { RAD, FS } from "../lib/tokens";
import { ModalLayout } from "./SharedUI";
import { useReadMark, usePageListContext, oneRow } from "../hooks/usePageList"; // 212 §22
import { ListLoadContext, PAGE_LISTS } from "../lib/listLoad";
import { withCount } from "../lib/pluralVars"; // 209 §7 — a sentence's number, read once, as its placeholder and as `count`
import { orderByClient } from "../lib/memberOrder"; // 216 §4 [RULED 71]

// Phase 22 — Collective contact-list builder.
//
// Read + filter/search + copy-to-clipboard / compose-handoff. LexCRM sends
// no mail, stores no record, generates no PDF, calls no M365. The modal
// filters the collective case's members across the combinable axes and
// produces two outputs: List A (Bcc address batches of ≤250 for Outlook) and
// List B (postal addressee lists for paper letters). A name-search field
// drives an individual compose handoff that reuses the same setMailDlg path
// the Parties section uses (Subject = case title, To = member email).
//
// Phase 25 — the segment predicates (docs-missing, docs-in-order, payment-due,
// status ternary) now live in the shared lib/collectiveSegments.mjs module and
// the filter UI in CollectiveSegmentFilter, both shared with the bulk-mailer.
// This modal's behaviour is unchanged: it still sends nothing.
//
// Gated upstream by the `collectiveCase.contactBuilder` section key — this
// component is only rendered when can() resolves true.

const BCC_BATCH_SIZE = 250; // keeps each Bcc copy-batch under Exchange Online's cap
const BCC_DELIM = "; "; // Outlook (desktop + web) recipient delimiter

type CollectiveContactListModalProps = {
  cc: any;
  clients?: any[];
  pname?: any;
  amountDueByClientId?: any;
  onClose?: any;
  onIndividualSend?: any;
  toast?: any;
};

export default function CollectiveContactListModal({
  cc, // the collective case (id, title, ref, clientLinks, rounds)
  clients = [], // full client list
  pname, // name display helper
  // C.2 — { [clientId]: amountDue } from POST /api/retainer/compute (the
  // round money moved server-side; CCV hands the modal the computed map)
  amountDueByClientId,
  onClose,
  onIndividualSend, // (clientObj) => void — triggers the shared setMailDlg path
  toast,
}: CollectiveContactListModalProps) {
  const { t } = useTranslation();

  const rounds = useMemo(() => cc.rounds || [], [cc]);
  const links = useMemo(() => cc.clientLinks || [], [cc]);

  // Member rows: {link, client, status}. Absent client (dangling clId) dropped.
  // 216 §4 [RULED 71] — the members in the one order, by their client (the roster's stored order until now)
  const members = useMemo(
    () =>
      orderByClient(
        links
          .map((lk: any) => {
            const client = clients.find((x: any) => x.id === lk.clId);
            return client ? { link: lk, client, status: lk.status } : null;
          })
          .filter(Boolean) as { link: unknown; client?: unknown; status?: unknown }[],
      ),
    [links, clients],
  );

  // ── Document tracking (docs axes source) ───────────────────────────────
  const [docTypes, setDocTypes] = useState<unknown[]>([]);
  const [docStatuses, setDocStatuses] = useState<unknown[]>([]);
  const [docLoaded, setDocLoaded] = useState(false);
  /* 212 §22 — asked the 210 §1 way; still failing, its mark stands where the
     documents' axes would (CollectiveSegmentFilter), with « Réessayer » —
     the axes no longer vanish without a word. */
  const { entry: docRead, again: docAgain, ask: askDocs } = useReadMark(PAGE_LISTS.docTracking);
  const docLists = usePageListContext(docRead);
  useEffect(() => {
    let cancelled = false;
    setDocLoaded(false);
    void askDocs("the document tracking", () => oneRow(api.get(`/collective-cases/${cc.id}/document-tracking`))).then((rows) => {
      if (cancelled) return;
      const data = (rows ? rows[0] : null) as { types?: unknown[]; statuses?: unknown[] } | null;
      setDocTypes(data?.types || []);
      setDocStatuses(data?.statuses || []);
      setDocLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [cc.id, docAgain, askDocs]);

  const docStatusIndex = useMemo(
    () => buildDocStatusIndex(docStatuses),
    [docStatuses],
  );

  // Single source of truth for the predicates (shared with the bulk-mailer).
  const evalr = useMemo(
    () =>
      makeSegmentEvaluator({
        docTypes,
        docStatusIndex,
        rounds,
        amountDueByClientId,
      }),
    [docTypes, docStatusIndex, rounds, amountDueByClientId],
  );

  const hasDocTypes = docLoaded && evalr.hasDocTypes; // axis availability (docs)
  const hasRounds = evalr.hasRounds; // axis availability (payment)

  // ── Filter controls ────────────────────────────────────────────────────
  const [filter, setFilter] = useState(defaultSegmentFilter());
  const patchFilter = (p: any) => setFilter((f) => ({ ...f, ...p }));

  // If an enabled axis becomes unavailable (no rounds / no doctypes), drop it.
  useEffect(() => {
    if (!hasDocTypes && (filter.useDocs || filter.useDocsInOrder))
      patchFilter({ useDocs: false, useDocsInOrder: false });
  }, [hasDocTypes]); // eslint-disable-line
  useEffect(() => {
    if (!hasRounds && filter.usePay) patchFilter({ usePay: false });
  }, [hasRounds]); // eslint-disable-line

  const segment = useMemo(
    () => evalr.resolve(members, filter),
    [members, filter, evalr],
  );

  // ── Outputs ────────────────────────────────────────────────────────────
  const withEmailMembers = segment.filter((m: any) => hasEmail(m.client));
  const withoutEmailMembers = segment.filter((m: any) => !hasEmail(m.client));

  // Unique (case-insensitive) addresses for the Bcc batches.
  const addresses = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const m of withEmailMembers) {
      const e = m.client.email.trim();
      const k = e.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(e);
    }
    return out;
    // eslint-disable-next-line
  }, [segment]);

  const batches = useMemo(() => chunk(addresses, BCC_BATCH_SIZE), [addresses]);

  const subject = `${cc.title || ""}${cc.ref ? " — " + cc.ref : ""}`.trim();

  const postalBlock = useMemo(() => {
    return withoutEmailMembers
      .map((m: any) => {
        const c = m.client;
        const lines = [
          addresseeName(c, pname),
          (c.street || "").trim(),
          [(c.postalCode || "").trim(), (c.city || "").trim()]
            .filter(Boolean)
            .join(" "),
          (c.country || "").trim(),
        ].filter(Boolean);
        return lines.join("\n");
      })
      .join("\n\n");
    // eslint-disable-next-line
  }, [segment]);

  // ── Clipboard ──────────────────────────────────────────────────────────
  async function copyText(text: any, label: any) {
    if (!text) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast &&
        toast(
          t("contactList.copied", { fallback: "Copied" }) +
            (label ? " — " + label : ""),
        );
    } catch (e) {
      toast && toast(t("common.failed", { fallback: "Failed" }));
    }
  }

  // ── Individual name-search ─────────────────────────────────────────────
  const [searchQ, setSearchQ] = useState("");
  const searchResults = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    if (!q) return [];
    return members
      .filter((m: any) => {
        const nm = (pname ? pname(m.client) : "").toLowerCase();
        const em = (m.client.email || "").toLowerCase();
        const co = (m.client.companyName || "").toLowerCase();
        return nm.includes(q) || em.includes(q) || co.includes(q);
      })
      .slice(0, 30);
    // eslint-disable-next-line
  }, [searchQ, members]);

  const lbl = { fontSize: FS.s11, color: "var(--tp)" };
  const muted = { fontSize: FS.s10, color: "var(--tm)" };
  const box = {
    border: "1px solid var(--br)",
    borderRadius: RAD.r6,
    padding: 10,
    marginBottom: 10,
    background: "var(--bi)",
  };
  const copyBtnStyle = {
    padding: "2px 7px",
    fontSize: FS.s10,
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
  };

  return (
    <ModalLayout
      onClose={onClose}
      closeOnOverlay
      stopShellClicks
      maxWidth={640}
      shellStyle={{ display: "flex", flexDirection: "column" }}
      rawBody
      headerContent={
        <div className="modal-header">
          <h3 style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Filter size={14} />
            {t("contactList.title", { fallback: "Contact list" })}
            {cc.title ? " — " + cc.title : ""}
          </h3>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onClose}
            style={{ padding: "2px 6px" }}
          >
            <X size={14} />
          </button>
        </div>
      }
      footer={
        <div className="modal-footer">
          <button type="button" className="modal-cancel" onClick={onClose}>
            {t("common.close", { fallback: "Close" })}
          </button>
        </div>
      }
    >

        <div style={{ padding: "14px 18px", overflowY: "auto" }}>
          {/* ── Filters (shared component) ────────────────────────────── */}
          <ListLoadContext.Provider value={docLists}>
            <CollectiveSegmentFilter
              filter={filter}
              onChange={patchFilter}
              hasDocTypes={hasDocTypes}
              hasRounds={hasRounds}
              docsList={PAGE_LISTS.docTracking}
            />
          </ListLoadContext.Provider>

          {/* ── Counts ───────────────────────────────────────────────── */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
            {[
              [t("contactList.count_selected", { fallback: "Selected" }), segment.length],
              [t("contactList.count_email", { fallback: "With email" }), withEmailMembers.length],
              [t("contactList.count_noemail", { fallback: "Without email" }), withoutEmailMembers.length],
              [t("contactList.count_batches", { fallback: "Batches" }), batches.length],
            ].map(([label, n]) => (
              <div
                key={label}
                style={{
                  flex: "1 1 110px",
                  border: "1px solid var(--br)",
                  borderRadius: RAD.r6,
                  padding: "8px 10px",
                  textAlign: "center",
                  background: "var(--bs)",
                }}
              >
                <div style={{ fontSize: FS.s18, fontWeight: 700, color: "var(--tp)" }}>{n}</div>
                <div style={muted}>{label}</div>
              </div>
            ))}
          </div>
          {addresses.length !== withEmailMembers.length && (
            <div style={{ ...muted, marginTop: -6, marginBottom: 10 }}>
              {/* 208 §20 — the number, which this call never passed (the seeded text printed « {n} »), and the form it picks */}
              {((merged: number) =>
                t("contactList.dedupe_note", {
                  n: merged,
                  count: merged,
                  fallback: `${String(merged)} duplicate addresses merged for Bcc.`,
                }))(Number(withEmailMembers.length) - addresses.length)}
            </div>
          )}

          {/* ── Subject ──────────────────────────────────────────────── */}
          <div style={box}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ ...muted, fontWeight: 600 }}>
                  {t("contactList.subject", { fallback: "Subject" })}
                </div>
                <div style={{ ...lbl, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {subject || "—"}
                </div>
              </div>
              <button
                className="btn btn-ghost"
                style={copyBtnStyle}
                onClick={() => copyText(subject, t("contactList.subject", { fallback: "Subject" }))}
              >
                <Copy size={11} />
                {t("common.copy", { fallback: "Copy" })}
              </button>
            </div>
          </div>

          {/* ── List A — Bcc batches ─────────────────────────────────── */}
          <div style={{ ...lbl, fontWeight: 600, margin: "4px 0 6px" }}>
            {t("contactList.list_a", {
              fallback: "List A — Bcc addresses (members hidden from each other)",
            })}
          </div>
          {batches.length === 0 ? (
            <div style={{ ...muted, marginBottom: 12 }}>
              {t("contactList.no_email", { fallback: "No selected members have an email address." })}
            </div>
          ) : (
            batches.map((b, i) => (
              <div key={i} style={box}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ ...lbl, fontWeight: 600 }}>
                    {t("contactList.batch_n", withCount(b.length, "k", { i: i + 1,
                      n: batches.length,
                      fallback: `Batch ${i + 1} of ${batches.length} (${b.length} address${b.length === 1 ? "" : "es"})`,
                    }))}
                  </span>
                  <button
                    className="btn btn-ghost"
                    style={copyBtnStyle}
                    onClick={() =>
                      copyText(
                        b.join(BCC_DELIM),
                        t("contactList.batch_label", { i: i + 1, fallback: `Batch ${i + 1}` }),
                      )
                    }
                  >
                    <Copy size={11} />
                    {t("contactList.copy_bcc", { fallback: "Copy Bcc" })}
                  </button>
                </div>
                <div
                  style={{
                    ...muted,
                    fontFamily: "monospace",
                    maxHeight: 70,
                    overflowY: "auto",
                    wordBreak: "break-all",
                    lineHeight: 1.5,
                  }}
                >
                  {b.join(BCC_DELIM)}
                </div>
              </div>
            ))
          )}

          {/* ── List B — postal ──────────────────────────────────────── */}
          <div style={{ ...lbl, fontWeight: 600, margin: "10px 0 6px" }}>
            {t("contactList.list_b", { fallback: "List B — Postal addresses (members with no email)" })}
          </div>
          {withoutEmailMembers.length === 0 ? (
            <div style={{ ...muted, marginBottom: 12 }}>
              {t("contactList.no_postal", { fallback: "Every selected member has an email address." })}
            </div>
          ) : (
            <div style={box}>
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}>
                <button
                  className="btn btn-ghost"
                  style={copyBtnStyle}
                  onClick={() =>
                    copyText(postalBlock, t("contactList.list_b_label", { fallback: "Postal addresses" }))
                  }
                >
                  <Copy size={11} />
                  {t("contactList.copy_postal", { fallback: "Copy list" })}
                </button>
              </div>
              <pre
                style={{
                  ...muted,
                  whiteSpace: "pre-wrap",
                  margin: 0,
                  maxHeight: 140,
                  overflowY: "auto",
                  fontFamily: "inherit",
                }}
              >
                {postalBlock}
              </pre>
            </div>
          )}

          {/* ── Individual send (name search) ────────────────────────── */}
          <div style={{ ...lbl, fontWeight: 600, margin: "10px 0 6px" }}>
            {t("contactList.individual", { fallback: "Send to one member" })}
          </div>
          <div style={{ position: "relative", marginBottom: 6 }}>
            <Search
              size={12}
              style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", color: "var(--tm)" }}
            />
            <input autoComplete="off" /* 207 §18 */
              className="field-input no-contact-autofill"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder={t("contactList.search_ph", { fallback: "Search a member by name or email…" })}
              style={{ paddingLeft: 26, width: "100%", fontSize: FS.s11 }}
            />
          </div>
          {searchQ.trim() && (
            <div style={{ border: "1px solid var(--br)", borderRadius: RAD.r6, maxHeight: 180, overflowY: "auto" }}>
              {searchResults.length === 0 ? (
                <div style={{ ...muted, padding: 8 }}>
                  {t("contactList.no_match", { fallback: "No member found." })}
                </div>
              ) : (
                searchResults.map((m: any) => {
                  const emailOk = hasEmail(m.client);
                  return (
                    <div
                      key={m.client.id}
                      style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 8px", borderBottom: "1px solid var(--br)" }}
                    >
                      <span style={{ ...lbl, fontWeight: 500, flex: 1, minWidth: 0 }}>
                        {pname ? pname(m.client) : m.client.email}
                      </span>
                      <span style={{ ...muted, flexShrink: 0 }}>
                        {m.client.email || t("contactList.no_email_tag", { fallback: "(no email)" })}
                      </span>
                      <button
                        className="btn btn-ghost"
                        disabled={!emailOk}
                        title={emailOk ? undefined : t("contactList.no_email_tag", { fallback: "(no email)" })}
                        style={{ ...copyBtnStyle, opacity: emailOk ? 1 : 0.4, cursor: emailOk ? "pointer" : "not-allowed" }}
                        onClick={() => emailOk && onIndividualSend && onIndividualSend(m.client)}
                      >
                        <Mail size={11} />
                        {t("contactList.compose", { fallback: "Compose" })}
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

    </ModalLayout>
  );
}
